import {
  BodyText,
  Button,
  Icon,
  IconSet,
  ProgressBar,
  Tooltip,
} from '@postscript/components';
import SidebarLauncherButton from 'components/navigation/SidebarLauncherButton';
import { useProgressiveOnboarding } from 'providers/ProgressiveOnboarding/ProgressiveOnboardingProvider';
import type { Step } from 'providers/ProgressiveOnboarding/types';
import React from 'react';
import styled from 'styled-components';

import { LAUNCHPAD_LABEL } from './constants';

const StyledProgressLauncher = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-1);
  width: 100%;
  padding: var(--spacing-2);
  border: 1px solid var(--border-color-dim);
  border-radius: var(--border-radius-small);
  background: var(--surface-bkg-color);
  color: var(--text-color);
  cursor: pointer;
  overflow: hidden;
  transition: border-color var(--hover-transition),
    background-color var(--hover-transition), color var(--hover-transition);
  text-align: left;

  &:hover {
    border-color: var(--link-color-hover);
  }

  &:active {
    border-color: var(--link-color-active);
  }

  &:focus-visible {
    outline: 2px solid var(--highlight-color);
    outline-offset: 2px;
  }
`;

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-1);
  width: 100%;
`;

const StyledLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font: var(--body-text-small);
  font-weight: var(--body-text-bold-font-weight);
`;

const StyledProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  width: 100%;
`;

const StyledProgressRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  width: 100%;
`;

const StyledProgressBarWrapper = styled.div`
  flex: 1;
  min-width: 0;
`;

export interface ProgressInfo {
  setupName: string;
  completedSteps: number;
  totalSteps: number;
  currentStep?: Step;
}

interface LaunchpadLauncherProps {
  isPanelOpen: boolean;
  onClick: () => void;
  progress?: ProgressInfo;
}

const LaunchpadLauncher = ({
  isPanelOpen,
  onClick,
  progress,
}: LaunchpadLauncherProps) => {
  const { dismissGuide, dismissedGuideKeys } = useProgressiveOnboarding();

  if (!progress) {
    return (
      <SidebarLauncherButton
        icon={IconSet.Compass}
        label={LAUNCHPAD_LABEL}
        isPanelOpen={isPanelOpen}
        onClick={onClick}
        data-cy="footer-launchpad-button"
        aria-label={LAUNCHPAD_LABEL}
      />
    );
  }

  const PromoCard =
    progress.currentStep && !dismissedGuideKeys.has(progress.currentStep.key)
      ? progress.currentStep.promoCard
      : undefined;

  const handleDismiss = () => {
    if (!progress || !progress.currentStep) return;

    dismissGuide({
      guideKey: progress.currentStep.key,
      setupName: progress.setupName,
    });
  };

  if (PromoCard) {
    return <PromoCard onDismiss={handleDismiss} />;
  }

  const handleHideProgress = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleDismiss();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <StyledProgressLauncher
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      data-cy="footer-launchpad-button"
      aria-label={LAUNCHPAD_LABEL}
    >
      <StyledHeader>
        <Icon component={IconSet.Compass} size={24} />
        <StyledLabel>{LAUNCHPAD_LABEL}</StyledLabel>
      </StyledHeader>

      <StyledProgressContainer>
        <BodyText size="x-small" color="var(--text-color-dim)">
          Continue {progress.setupName}
        </BodyText>

        <StyledProgressRow>
          <StyledProgressBarWrapper>
            <ProgressBar
              step={progress.completedSteps + 1} // Displays the step the user is ON, rather than what they've completed
              totalSteps={progress.totalSteps}
            />
          </StyledProgressBarWrapper>
          <span data-tip data-for="hide-progress-tooltip">
            <Button
              variant="text"
              size="small"
              icon={IconSet.Close}
              onClick={handleHideProgress}
              aria-label="Hide progress"
            />
          </span>
          <Tooltip id="hide-progress-tooltip" size="small">
            Hide
          </Tooltip>
        </StyledProgressRow>
      </StyledProgressContainer>
    </StyledProgressLauncher>
  );
};

export default LaunchpadLauncher;
